// Where a browser keeps the account vault key (PROTOCOL.md §2). The client library works on raw key bytes, so the
// AVK can't live in IndexedDB as a non-extractable CryptoKey itself; instead it's sealed with a device key that is
// one (AES-256-GCM, `extractable: false`, made once per browser), and unsealed into memory when the page loads.
// A script that runs in the page can still use the device key, but the stored bytes are useless copied elsewhere.
//
// “Don’t keep the vault key on this computer” keeps it in memory only (gone on reload). Signing out wipes both the
// sealed copy and the device key. Every storage call is guarded: private windows may refuse IndexedDB, and then the
// key simply isn't kept.
import { utf8 } from '../shared/b64.ts';

type Bytes = Uint8Array<ArrayBuffer>;

/** The little a keystore needs from storage (IndexedDB in the page, a Map in tests). */
export interface KeyBackend {
  get(key: string): Promise<unknown>;
  put(key: string, value: unknown): Promise<void>;
  delete(key: string): Promise<void>;
}

const DEVICE_KEY = 'device-key';
const VAULT = 'vault';
const MEMORY_ONLY = 'memory-only';
const aad = (accountId: string) => utf8(`writing-suite/web-keystore/v1:${accountId}`);

interface SealedVault {
  v: 1;
  accountId: string;
  iv: Bytes;
  data: ArrayBuffer;
}

export class Keystore {
  constructor(private readonly backend: KeyBackend | null) {}

  /** Whether keys can be kept at all (false in a private window that refuses IndexedDB). */
  get persistent(): boolean {
    return this.backend !== null;
  }

  private async read(key: string): Promise<unknown> {
    try {
      return (await this.backend?.get(key)) ?? null;
    } catch {
      return null;
    }
  }

  private async write(key: string, value: unknown): Promise<boolean> {
    if (!this.backend) return false;
    try {
      await this.backend.put(key, value);
      return true;
    } catch {
      return false;
    }
  }

  private async remove(key: string): Promise<void> {
    try {
      await this.backend?.delete(key);
    } catch {
      // nothing kept, or storage refused: either way nothing to remove
    }
  }

  async memoryOnly(): Promise<boolean> {
    return !this.backend || (await this.read(MEMORY_ONLY)) === true;
  }

  /** Switches memory-only on (removing any kept copy) or off (keeping `current`, when given). */
  async setMemoryOnly(on: boolean, current?: { accountId: string; avk: Uint8Array }): Promise<void> {
    await this.write(MEMORY_ONLY, on);
    if (on) await this.remove(VAULT);
    else if (current) await this.keep(current.accountId, current.avk);
  }

  private async deviceKey(create: boolean): Promise<CryptoKey | null> {
    const stored = await this.read(DEVICE_KEY);
    if (stored instanceof CryptoKey) return stored;
    if (!create || !this.backend) return null;
    const key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
    return (await this.write(DEVICE_KEY, key)) ? key : null;
  }

  /** Keeps the AVK sealed under this browser's device key, unless memory-only. Returns whether it was kept. */
  async keep(accountId: string, avk: Uint8Array): Promise<boolean> {
    if (await this.memoryOnly()) return false;
    const key = await this.deviceKey(true);
    if (!key) return false;
    try {
      const iv = crypto.getRandomValues(new Uint8Array(12));
      const data = await crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: aad(accountId) }, key, avk as Bytes);
      return this.write(VAULT, { v: 1, accountId, iv, data } satisfies SealedVault);
    } catch {
      return false;
    }
  }

  /** The AVK kept for this account, or null (none kept, another account's, memory-only, or unreadable). */
  async recall(accountId: string): Promise<Bytes | null> {
    if (await this.memoryOnly()) return null;
    const sealed = (await this.read(VAULT)) as SealedVault | null;
    if (!sealed || sealed.v !== 1 || sealed.accountId !== accountId) return null;
    const key = await this.deviceKey(false);
    if (!key) return null;
    try {
      const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: sealed.iv, additionalData: aad(accountId) }, key, sealed.data);
      return plain.byteLength === 32 ? new Uint8Array(plain) : null;
    } catch {
      return null;
    }
  }

  /** Signing out: the sealed copy and the device key both go (the memory-only choice stays). */
  async wipe(): Promise<void> {
    await this.remove(VAULT);
    await this.remove(DEVICE_KEY);
  }
}

/** IndexedDB behind a keystore, or null when this browser won't open it (some private windows). */
export async function indexedDBBackend(name = 'writing-suite', store = 'keys'): Promise<KeyBackend | null> {
  let db: IDBDatabase;
  try {
    if (typeof indexedDB === 'undefined') return null;
    db = await new Promise<IDBDatabase>((resolve, reject) => {
      const req = indexedDB.open(name, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(store);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
      req.onblocked = () => reject(new Error('IndexedDB blocked'));
    });
  } catch {
    return null;
  }
  // Another page deleting the database (an account deleted, PROTOCOL.md §9.6) closes this connection, so the deletion
  // isn't left waiting on it.
  db.onversionchange = () => db.close();
  const run = <T>(mode: IDBTransactionMode, op: (s: IDBObjectStore) => IDBRequest<T>) =>
    new Promise<T>((resolve, reject) => {
      const tx = db.transaction(store, mode);
      const req = op(tx.objectStore(store));
      tx.oncomplete = () => resolve(req.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  return {
    get: (key) => run('readonly', (s) => s.get(key)),
    put: async (key, value) => void (await run('readwrite', (s) => s.put(value, key))),
    delete: async (key) => void (await run('readwrite', (s) => s.delete(key))),
  };
}
